// In Results.tsx

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { IResultsProps } from './IResults';
// import { useSelector } from 'react-redux'; // useAppSelector is preferred
import { Grid, Typography, Paper, Card, Table, TableBody, TableCell, TableHead, TableRow, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RootState } from '../../../../reduxService'; // Adatta
import { useTranslation } from 'react-i18next';
import TireAndLayersAirportPaved, { ILayerData, IGeogridInfo } from './TireAndLayersAirport'; // Importa TireAndLayersAirport
import { unit as unitEnum, EProjectType } from '../../../enum/project'; // Adatta, Import EProjectType
// import { selectDynamicProject } from '../../../Dynamic/store/project/projectslice'; // No longer needed directly if using dynamicProject from RootState
import PavementLineChartComponent from './PavementLineChartComponent';
import { getActionByProjectType } from '../../../actionMap';
import { useParams } from 'react-router-dom';
// import { selectProjectInfo } from '../../../store/projectInfo/projectInfoSlice'; // No longer needed if projectInfo comes from dynamicProject
import { useAppSelector, useAppDispatch } from '../../../../reduxService/hooks';
import { conversionLabel } from '../../../conversion';
import './Results.scss';
import { logDebug, logWarn, logError, LogTag } from '../../../utils/utils';
// Use IContainerYardData as suggested by error, and ensure IDynamicProjectOutputData is correctly defined in IApiResponse.ts
import {
    IContainerYardData,
    IDynamicProjectOutputData,
    IBCRData,
    IDeckAnalysisDataItem,
    IParkingDeckData,
    ILoadDataWheel,
    IStaticLoadData,
    IUnpavedData,
    IPavedData,
    IAASHTOResults,
} from '../../../api/IApiResponse'; // Added IParkingDeckData, ILoadDataWheel, IStaticLoadData, IUnpavedData, IPavedData, IAsphaltData, IRoadInfo, ITrafficLoad
import { setPavedCalculatedDesignThickness } from '../../../Dynamic/store/project/projectslice';
import CustomTable from '../../../components/Layout/CustomTable/CustomTable';
import { UNIT_CONVERSION, DEFAULT_LCR } from '../../../constants/AASHTOConstants';
import convert from 'convert-units';

interface Point {
    x: number;
    y: number;
}

// Extend IDeckAnalysisDataItem locally to include optional bcr for this chart logic
interface IDeckAnalysisDataItemWithBcr extends IDeckAnalysisDataItem {
    bcr?: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Results_Airport = (props: IResultsProps) => {
    const rawOutput = useAppSelector((state: RootState) => state.Dynamic.results.data?.outputData as IDynamicProjectOutputData | undefined);
    const aashtoResults = useAppSelector((state: RootState) => state.Dynamic.project.data?.aashtoResults as IAASHTOResults | undefined);
    // const projectInfo = useAppSelector(selectProjectInfo); // Sourced from dynamicProject now
    const dynamicProject = useAppSelector((state: RootState) => state.Dynamic.project); // Get the whole dynamicProject state
    const { projectType: routeProjectType } = useParams(); // Renamed to avoid conflict
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // Debug aashtoResults structure
    useEffect(() => {
        console.log('🔍 Results_Airport: AASHTO results received', {
            hasResults: !!aashtoResults,
            baseValues: {
                stabilised: aashtoResults?.detailedCalculations?.softwareDesign?.base?.stabilised,
                unstabilised: aashtoResults?.detailedCalculations?.softwareDesign?.base?.unstabilised,
            },
            fullSoftwareDesign: aashtoResults?.detailedCalculations?.softwareDesign,
        });
    }, [aashtoResults]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [unreinforcedStageHeight, setUnreinforcedStageHeight] = useState<number | undefined>(undefined);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [unreinforcedSubgradeY, setUnreinforcedSubgradeY] = useState<number | undefined>(undefined);

    // Get thickness inputs from form data instead of local state
    const projectData = dynamicProject.data?.projectInfo?.data as IPavedData;

    // Check if design analysis are enabled via checkboxes
    const isSurfaceDesignEnabled = projectData?.surfaceDesign?.enableSurfaceDesign === 'yes';
    const isBaseDesignEnabled = projectData?.baseDesign?.enableBaseDesign === 'yes';
    const isSubbaseDesignEnabled = projectData?.subbaseDesign?.enableSubbaseDesign === 'yes';

    // ESAL mode and value for conditional rendering (aligned with Paved)
    const esalMode = projectData?.trafficLoad?.esalCalculationMode || 'manual';
    const esalFinalValue = projectData?.trafficLoad?.esalFinalValue;

    // Multi-layer geogrid detection WITHOUT value fallback (Airport-specific)
    // Layer 1: Form field | Layer 2: Geogrid array | Layer 3: DISABLED (critical difference from Paved)
    const hasBaseGeogrid = React.useMemo(() => {
        // Layer 1: Form field (primary source)
        const fromForm = Boolean(projectData?.baseCourse?.baseGeogrid && projectData.baseCourse.baseGeogrid !== 'none_selected');

        // Layer 2: Geogrid array (secondary source)
        const fromArray = Boolean(
            projectData?.geoGrid?.geogridBaseCourse?.geogrids &&
            projectData.geoGrid.geogridBaseCourse.geogrids.length > 0 &&
            projectData.geoGrid.geogridBaseCourse.geogrids[0]?.selectedGeogrid !== 'none_selected'
        );

        // Layer 3: Backend calculated values (DISABLED for Airport - see reason below)
        const stabilised = aashtoResults?.detailedCalculations?.softwareDesign?.base?.stabilised;
        const unstabilised = aashtoResults?.detailedCalculations?.softwareDesign?.base?.unstabilised;
        const fromValues = stabilised !== undefined && unstabilised !== undefined && stabilised !== unstabilised;

        console.log('🔍 AIRPORT Geogrid Detection:', {
            projectType: 'Airport',
            layer1_formField: {
                exists: !!projectData?.baseCourse?.baseGeogrid,
                value: projectData?.baseCourse?.baseGeogrid,
                result: fromForm,
            },
            layer2_geogridArray: {
                exists: !!projectData?.geoGrid?.geogridBaseCourse?.geogrids,
                length: projectData?.geoGrid?.geogridBaseCourse?.geogrids?.length,
                firstGeogrid: projectData?.geoGrid?.geogridBaseCourse?.geogrids?.[0]?.selectedGeogrid,
                result: fromArray,
            },
            layer3_valueComparison: {
                stabilised,
                unstabilised,
                different: fromValues,
                note: 'DISABLED for Airport - would cause false positives',
            },
            finalDecision: fromForm || fromArray,
        });

        if (fromForm) return true;
        if (fromArray) return true;

        // Layer 3: DISABLED for Airport (VERIFIED with testing)
        // CRITICAL: Airport calculates different stabilised/unstabilised values REGARDLESS of geogrid:
        // - WITHOUT geogrid: stabilised=0.97m, unstabilised=0.49m (different, but no geogrid!)
        // - WITH geogrid: stabilised=0.63m, unstabilised=0.49m (different, with geogrid)
        // The stabilised/unstabilised fields represent TRAFFIC/DESIGN METHOD, not geogrid presence
        // Therefore value comparison would cause false positives - Layer 3 MUST stay disabled
        return false;
    }, [projectData?.baseCourse?.baseGeogrid, projectData?.geoGrid?.geogridBaseCourse?.geogrids, aashtoResults?.detailedCalculations?.softwareDesign?.base]);

    const hasSubbaseGeogrid = React.useMemo(() => {
        // Layer 1: Form field (primary source)
        const fromForm = Boolean(projectData?.subbaseCourse?.subbaseGeogrid && projectData.subbaseCourse.subbaseGeogrid !== 'none_selected');
        if (fromForm) return true;

        // Layer 2: Geogrid array (secondary source)
        const fromArray = Boolean(
            projectData?.geoGrid?.geogridSubBaseCourse?.geogrids &&
            projectData.geoGrid.geogridSubBaseCourse.geogrids.length > 0 &&
            projectData.geoGrid.geogridSubBaseCourse.geogrids[0]?.selectedGeogrid !== 'none_selected'
        );
        if (fromArray) return true;

        // Layer 3: DISABLED for Airport (same reason as hasBaseGeogrid)
        return false;
    }, [projectData?.subbaseCourse?.subbaseGeogrid, projectData?.geoGrid?.geogridSubBaseCourse?.geogrids]);

    // Surface Design inputs (for calculating surface thickness)
    const surfaceDesignInputs = useMemo(
        () => ({
            baseThickness: projectData?.surfaceDesign?.baseThickness || 0.2,
            subbaseThickness: projectData?.surfaceDesign?.subbaseThickness || 0.4,
        }),
        [projectData?.surfaceDesign?.baseThickness, projectData?.surfaceDesign?.subbaseThickness]
    );

    // Base Design inputs (for calculating base thickness)
    const baseDesignInputs = useMemo(
        () => ({
            surfaceThickness: projectData?.baseDesign?.surfaceThickness || 0.1,
            subbaseThickness: projectData?.baseDesign?.subbaseThickness || 0.4,
        }),
        [projectData?.baseDesign?.surfaceThickness, projectData?.baseDesign?.subbaseThickness]
    );

    // Subbase Design inputs (for calculating subbase thickness)
    const subbaseDesignInputs = useMemo(
        () => ({
            surfaceThickness: projectData?.subbaseDesign?.surfaceThickness || 0.1,
            baseThickness: projectData?.subbaseDesign?.baseThickness || 0.2,
        }),
        [projectData?.subbaseDesign?.surfaceThickness, projectData?.subbaseDesign?.baseThickness]
    ); // Removed useEffect that was overriding Excel default values with Goal Seek results
    // Focus Design should maintain the Excel default values (0.10, 0.20, 0.40) and allow user modifications

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { graphic } = getActionByProjectType((routeProjectType as EProjectType) || EProjectType.container_yard);
    const [unreinforcedDeckTable, setUnreinforcedDeckTable] = useState<Point[]>([]);
    const [reinforcedDeckTable, setReinforcedDeckTable] = useState<Point[]>([]);
    const [bcrDeckTable, setBcrDeckTable] = useState<Point[]>([]);

    const currentProjectType = dynamicProject.data?.type; // Get project type
    //get project method
    const currentProjectMethod = dynamicProject.data?.method; // Get project method
    logDebug('PavResults', 'Current project type:', currentProjectType, LogTag.PROJECT_INFO);
    logDebug('PavResults', 'Current project method:', currentProjectMethod, LogTag.PROJECT_INFO);

    // Extract drainage primitives to avoid depending on entire dynamicProject.data object
    const drainageBelowBase = (dynamicProject.data?.projectInfo?.data as IPavedData)?.drainageDesign?.drainageBelowBase;
    const drainageBelowSubbase = (dynamicProject.data?.projectInfo?.data as IPavedData)?.drainageDesign?.drainageBelowSubbase;

    // Function to recalculate thickness based on AASHTO logic from CALCULATION.txt
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const recalculateThickness = React.useCallback(
        (scenario: 'surface' | 'base' | 'subbase', inputs: Record<string, number>, stabilised = false) => {
            if (!aashtoResults?.detailedCalculations) return 0;

            // Use the Goal Seek calculated totalSN instead of Excel cell calculator SN
            const structuralNumberSN = aashtoResults.totalSN;
            const { surface, base, subbase } = aashtoResults.detailedCalculations.layerCoefficients;

            // Check if GCD is used for Base and Subbase from extracted primitives
            const isGcdUsedForBase = drainageBelowBase === 'yes';
            const isGcdUsedForSubbase = drainageBelowSubbase === 'yes';

            const totalSN = structuralNumberSN;

            // Layer coefficients
            const a1 = surface.a1;
            const a2 = base.a2;
            const a3 = subbase.a3;

            // Use dynamic drainage coefficients from the main calculation results
            // This ensures Focus Design uses the same material-specific drainage quality as the main calculation
            const m2_dynamic = base.m2; // Dynamic coefficient from DrainageCoefficientService (e.g., 1.2 for "Crushed gravel 2#")
            const m3_dynamic = subbase.m3; // Dynamic coefficient from DrainageCoefficientService
            const m2_stabilised_gcd = 1.0; // From DRAINAGE_m!C27 (stabilised with GCD)
            const m3_stabilised_gcd = 0.75; // From DRAINAGE_m!C52 (stabilised with GCD)

            // Determine the correct m-value based on stabilisation and GCD usage
            const m2 = stabilised && isGcdUsedForBase ? m2_stabilised_gcd : m2_dynamic;
            const m3 = stabilised && isGcdUsedForSubbase ? m3_stabilised_gcd : m3_dynamic;

            // LCR values for stabilised calculations
            // Airport uses different geogrid products than Paved:
            // - Base: MacGrid EG 30S (1.588617) vs Paved's EG 20S (1.54147)
            // - Subbase: MacGrid EG 40S (1.67984) vs Paved's EG 30S (1.588617)
            const lcrBase = base.lcr || DEFAULT_LCR.SUBBASE_EG30S; // Airport uses EG 30S for base
            const lcrSubbase = subbase.lcr || DEFAULT_LCR.SUBBASE_EG40S; // Airport uses EG 40S for subbase

            let calculatedThicknessMeters = 0;

            if (scenario === 'surface') {
                // CALCULATION.txt C19/E19
                const baseThickness_in = inputs.baseThickness / UNIT_CONVERSION.METERS_TO_INCHES;
                const subbaseThickness_in = inputs.subbaseThickness / UNIT_CONVERSION.METERS_TO_INCHES;
                const baseSN = a2 * baseThickness_in * m2 * (stabilised ? lcrBase : 1);
                const subbaseSN = a3 * subbaseThickness_in * m3 * (stabilised ? lcrSubbase : 1);
                const remainingSN = totalSN - baseSN - subbaseSN;
                const surfaceThickness_in = remainingSN / a1;
                calculatedThicknessMeters = surfaceThickness_in * UNIT_CONVERSION.INCHES_TO_METERS;
            } else if (scenario === 'base') {
                // CALCULATION.txt C32/E32
                const surfaceThickness_in = inputs.surfaceThickness / UNIT_CONVERSION.METERS_TO_INCHES;
                const subbaseThickness_in = inputs.subbaseThickness / UNIT_CONVERSION.METERS_TO_INCHES;
                const surfaceSN = a1 * surfaceThickness_in;
                const subbaseSN = a3 * subbaseThickness_in * m3 * (stabilised ? lcrSubbase : 1);
                const remainingSN = totalSN - surfaceSN - subbaseSN;
                const divisor = a2 * m2 * (stabilised ? lcrBase : 1);
                const baseThickness_in = divisor > 0 ? remainingSN / divisor : 0;
                calculatedThicknessMeters = baseThickness_in * UNIT_CONVERSION.INCHES_TO_METERS;
            } else if (scenario === 'subbase') {
                // CALCULATION.txt C45/E45
                const surfaceThickness_in = inputs.surfaceThickness / UNIT_CONVERSION.METERS_TO_INCHES;
                const baseThickness_in = inputs.baseThickness / UNIT_CONVERSION.METERS_TO_INCHES;
                const surfaceSN = a1 * surfaceThickness_in;
                const baseSN = a2 * baseThickness_in * m2 * (stabilised ? lcrBase : 1);
                const remainingSN = totalSN - surfaceSN - baseSN;
                const divisor = a3 * m3 * (stabilised ? lcrSubbase : 1);
                const subbaseThickness_in = divisor > 0 ? remainingSN / divisor : 0;
                calculatedThicknessMeters = subbaseThickness_in * UNIT_CONVERSION.INCHES_TO_METERS;
            }

            // Replicate Excel's ROUNDUP(value, 2)
            return Math.max(0, Math.ceil(calculatedThicknessMeters * 100) / 100);
        },
        [aashtoResults, drainageBelowBase, drainageBelowSubbase]
    );

    // Funzione per calcolare dinamicamente il numero di passaggi in base allo spessore
    const calculateDynamicPasses = (scenario: 'surface' | 'base' | 'subbase', inputs: Record<string, number>, stabilised = false) => {
        if (!aashtoResults?.detailedCalculations) return 0;

        // Calcola lo spessore totale per lo scenario corrente
        let totalThicknessCurrentScenario = 0;
        let totalThicknessOriginal = 0;

        if (scenario === 'surface') {
            // Surface Design: surface calcolato + base e subbase fissi
            const surfaceThickness = recalculateThickness('surface', inputs, stabilised);
            totalThicknessCurrentScenario = surfaceThickness + inputs.baseThickness + inputs.subbaseThickness;

            // Original: usa gli spessori del software design originale
            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        } else if (scenario === 'base') {
            // Base Design: base calcolato + surface e subbase fissi
            const baseThickness = recalculateThickness('base', inputs, stabilised);
            totalThicknessCurrentScenario = inputs.surfaceThickness + baseThickness + inputs.subbaseThickness;

            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        } else if (scenario === 'subbase') {
            // Subbase Design: subbase calcolato + surface e base fissi
            const subbaseThickness = recalculateThickness('subbase', inputs, stabilised);
            totalThicknessCurrentScenario = inputs.surfaceThickness + inputs.baseThickness + subbaseThickness;

            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        }

        // Il numero di passaggi base dal progetto
        const basePassesFirstYear = projectData?.trafficLoad?.esalFinalValue || aashtoResults.detailedCalculations.esalFactors.esalUnstabilised;

        // Calcola il rapporto di spessore totale
        const thicknessRatio = totalThicknessOriginal > 0 ? totalThicknessCurrentScenario / totalThicknessOriginal : 1;

        // Più spesso = più traffico possibile (rapporto > 1 = più traffico)
        // Più sottile = meno traffico possibile (rapporto < 1 = meno traffico)
        let dynamicPasses = basePassesFirstYear * thicknessRatio;

        // Per il caso stabilizzato, applica anche il TBR
        if (stabilised) {
            const tbr = aashtoResults.detailedCalculations.esalFactors.tbr;
            dynamicPasses *= tbr;
        }

        return Math.round(dynamicPasses);
    };

    // Funzione per calcolare il W18 dinamico in base ai passaggi
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const calculateDynamicW18 = (dynamicPasses: number, _stabilised = false) => {
        if (!aashtoResults?.detailedCalculations) return 0;

        // Calcola il W18 basato sui passaggi dinamici
        const originalPasses = projectData?.trafficLoad?.esalFinalValue || aashtoResults.detailedCalculations.esalFactors.esalUnstabilised;
        const originalW18 = aashtoResults.detailedCalculations.esalFactors.logW18Unstabilised;

        if (originalPasses === 0) return originalW18;

        // Calcola il rapporto e aggiusta il W18 di conseguenza
        const passesRatio = dynamicPasses / originalPasses;
        const dynamicW18 = originalW18 + Math.log10(passesRatio);

        return dynamicW18;
    };

    /**
     * Calcola e salva le thickness del design selezionato per il GeoGrid mapper.
     * Se più design sono abilitati, prende il primo nell'ordine: surface -> base -> subbase.
     * Le thickness calcolate vengono salvate in Redux per essere utilizzate dal GeoGridDataMapper.
     */
    const calculateAndSaveSelectedDesignThickness = useCallback(() => {
        if (!aashtoResults?.detailedCalculations || !projectData) {
            return;
        }

        // Determina quale design è selezionato (priorità: surface -> base -> subbase)
        let selectedDesignType: 'surface' | 'base' | 'subbase' | 'standard' = 'standard';
        let calculatedThickness = {
            surface: 0,
            base: 0,
            subbase: 0,
        };

        if (isSurfaceDesignEnabled) {
            // Surface Design: surface calcolato, base e subbase fissi
            selectedDesignType = 'surface';
            calculatedThickness = {
                surface: recalculateThickness('surface', surfaceDesignInputs, false), // No geogrid in surface layer
                base: surfaceDesignInputs.baseThickness,
                subbase: surfaceDesignInputs.subbaseThickness,
            };
        } else if (isBaseDesignEnabled) {
            // Base Design: base calcolato, surface e subbase fissi
            selectedDesignType = 'base';
            calculatedThickness = {
                surface: baseDesignInputs.surfaceThickness,
                base: recalculateThickness('base', baseDesignInputs, hasBaseGeogrid), // Stabilised only if geogrid present
                subbase: baseDesignInputs.subbaseThickness,
            };
        } else if (isSubbaseDesignEnabled) {
            // Subbase Design: subbase calcolato, surface e base fissi
            selectedDesignType = 'subbase';
            calculatedThickness = {
                surface: subbaseDesignInputs.surfaceThickness,
                base: subbaseDesignInputs.baseThickness,
                subbase: recalculateThickness('subbase', subbaseDesignInputs, hasSubbaseGeogrid), // Stabilised only if geogrid present
            };
        } else {
            // Nessun design abilitato, usa i valori standard dal software design
            selectedDesignType = 'standard';
            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                calculatedThickness = {
                    surface: softwareDesign.surface.stabilised,
                    base: softwareDesign.base.stabilised,
                    subbase: softwareDesign.subbase.stabilised,
                };
            }
        }

        // Controlla se i dati sono già aggiornati per evitare loop infiniti
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const existingCalculatedThickness = (projectData as any)?.calculatedDesignThickness;
        const isAlreadyCalculated =
            existingCalculatedThickness &&
            existingCalculatedThickness.selectedDesignType === selectedDesignType &&
            Math.abs(existingCalculatedThickness.thickness.surface - calculatedThickness.surface) < 0.001 &&
            Math.abs(existingCalculatedThickness.thickness.base - calculatedThickness.base) < 0.001 &&
            Math.abs(existingCalculatedThickness.thickness.subbase - calculatedThickness.subbase) < 0.001;

        // A NaN never matches the check above: it would reach Redux as the design thickness
        const isFiniteThickness = [calculatedThickness.surface, calculatedThickness.base, calculatedThickness.subbase].every(Number.isFinite);

        if (isAlreadyCalculated || !isFiniteThickness) {
            return;
        }

        // Salva i dati calcolati in Redux
        dispatch(
            setPavedCalculatedDesignThickness({
                selectedDesignType,
                thickness: calculatedThickness,
                isStabilised: true, // Per il GeoGrid usiamo sempre i valori stabilised
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        aashtoResults,
        recalculateThickness,
        // Nota: escludiamo projectData dalle dipendenze per evitare loop infiniti quando si aggiorna calculatedDesignThickness
        isSurfaceDesignEnabled,
        isBaseDesignEnabled,
        isSubbaseDesignEnabled,
        surfaceDesignInputs,
        baseDesignInputs,
        subbaseDesignInputs,
        dispatch,
    ]);

    // Esegui il calcolo ogni volta che i dati AASHTO cambiano, ma non quando projectData cambia per evitare loop
    useEffect(() => {
        if (aashtoResults?.detailedCalculations && projectData) {
            calculateAndSaveSelectedDesignThickness();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        aashtoResults?.detailedCalculations,
        isSurfaceDesignEnabled,
        isBaseDesignEnabled,
        isSubbaseDesignEnabled,
        surfaceDesignInputs,
        baseDesignInputs,
        subbaseDesignInputs,
        calculateAndSaveSelectedDesignThickness,
    ]);

    // --- Check if geotextile was selected (for showing geotextile diagram) ---
    const hasGeotextileSelected = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved) return false;
        const unpavedData = dynamicProject.data?.projectInfo?.data as IUnpavedData;
        if (!unpavedData) return false;
        // Check both giroud and leng methods for selectedGeotextile in geoTextile group
        const method = currentProjectMethod || 'giroud';
        const methodData = method === 'giroud' ? unpavedData.giroud : unpavedData.leng;
        // The form data structure has geoTextile group with selectedGeotextile field
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedGeotextile = (methodData as any)?.geoTextile?.selectedGeotextile;
        logDebug(
            'PavResults',
            'Checking geotextile selection:',
            {
                method,
                selectedGeotextile,
                hasSelection: selectedGeotextile && selectedGeotextile.trim() !== '',
            },
            LogTag.VALIDATION
        );
        return selectedGeotextile && selectedGeotextile.trim() !== '';
    }, [currentProjectType, currentProjectMethod, dynamicProject.data?.projectInfo?.data]);

    // 🆕 SIMPLIFIED APPROACH: Redux contains data already in the correct unit
    // No need for conversion - the data from Redux is already in the display unit
    const processedOutput = useMemo(() => {
        logDebug('PavResults', 'Using output data directly from API - conversion handled at Redux level', undefined, LogTag.UNIT_CONVERSION);
        return rawOutput;
    }, [rawOutput]);

    logDebug('PavResults', 'Output for parking deck check:', processedOutput?.deckDesign, LogTag.DATA_TRANSFORM);

    // Source inputs from dynamicProject.data.projectInfo.data
    const projectInputs = useMemo(() => {
        if (dynamicProject.data?.type === EProjectType.container_yard) {
            return dynamicProject.data.projectInfo?.data as IContainerYardData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.parking_deck) {
            return dynamicProject.data.projectInfo?.data as IParkingDeckData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.unpaved) {
            return dynamicProject.data.projectInfo?.data;
        }
        return undefined;
    }, [dynamicProject.data]);

    const getMeasurementUnit = React.useCallback(
        (unit: string[]) => {
            const unitSystem = dynamicProject.data?.unit || 'Metric';
            return unit[unitSystem === 'Metric' ? 0 : 1];
        },
        [dynamicProject.data?.unit]
    );

    logDebug('PavResults', 'Output data:', processedOutput, LogTag.DATA_TRANSFORM);
    logDebug('PavResults', 'Project inputs from dynamicProject:', projectInputs, LogTag.DATA_TRANSFORM);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const loadResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data
        if (currentProjectType === EProjectType.unpaved) {
            // FLAT structure: load data is directly on projectInputs
            const unpavedData = projectInputs as Record<string, any>;
            if (!unpavedData?.load) return [];

            return [
                [t('design_axle_load', 'Design Axle Load'), 'W', unpavedData.load.designAxleLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('tyre_inflation_pressure', 'Tyre Inflation Pressure'), 'p', unpavedData.load.tyreInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
                [t('equivalent_standard_axle_load', 'ESAL'), 'Pa', unpavedData.load.esal?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('number_of_passages', 'Number of Passages'), 'Np', unpavedData.load.numPassages?.toString() || '', ''],
                [t('allowable_rut_depth', 'Allowable Rut Depth'), 's', unpavedData.load.allowableRutDepth?.toString() || '', 'mm'],
            ];
        }

        const bcrData = (projectInputs as Record<string, any>).bcr;
        if (!bcrData) return [];

        if (currentProjectType === EProjectType.container_yard) {
            const cyBcrLoad = (bcrData as IBCRData)?.load;
            return [
                [t('deckWidth'), 'B', cyBcrLoad?.deckWidth?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [t('deckLength'), 'L', cyBcrLoad?.deckLength?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [t('uniformLoadRes'), 'q', cyBcrLoad?.uniformLoad?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
                [t('safetyFactor'), 'SF', bcrData?.fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        } else if (currentProjectType === EProjectType.parking_deck) {
            const pdBcrLoad = bcrData.load as ILoadDataWheel; // Type assertion
            return [
                [t('wheel_load', 'Wheel Load'), 'P', pdBcrLoad?.wheelLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('tire_inflation_pressure', 'Tire Inflation Pressure'), 'p', pdBcrLoad?.tireInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
                [t('safetyFactor'), 'SF', bcrData.fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        }
        return [];
    }, [projectInputs, currentProjectType, currentProjectMethod, t, getMeasurementUnit]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const subgradeResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data
        if (currentProjectType === EProjectType.unpaved) {
            // FLAT structure: subgrade data is directly on projectInputs
            const unpavedData = projectInputs as Record<string, any>;
            if (!unpavedData?.subgrade) return [];

            const subgradeData = unpavedData.subgrade;
            const inputMethod = subgradeData.subgradeInputMethod;

            if (inputMethod === 'cbr') {
                return [
                    [t('cbr_base_course', 'CBR Base Course'), 'CBRbc', subgradeData.cbrBaseCourse?.toString() || '', '%'],
                    [t('cbr_subgrade', 'CBR Subgrade'), 'CBRsg', subgradeData.cbrSubgrade?.toString() || '', '%'],
                ];
            } else if (inputMethod === 'modulus') {
                return [
                    [t('ev2_base_course', 'Ev2 Base Course'), 'Ev2bc', subgradeData.ev2BaseCourse?.toString() || '', 'MPa'],
                    [t('ev2_subgrade', 'Ev2 Subgrade'), 'Ev2sg', subgradeData.ev2Subgrade?.toString() || '', 'MPa'],
                ];
            }
            return [];
        }

        const bcrData = (projectInputs as Record<string, any>).bcr;
        if (!bcrData) return [];

        // Access subgrade data using the correct runtime key 'subgradeCharacteristics'
        // The interface ISubgradeCharatteristic might have a typo, so we cast to 'any' for this access.
        const bcrSubgrade = (bcrData as Record<string, any>).subgradeCharacteristics;
        const bcrEmbarkment = bcrData.embankmentCharacteristics; // Assuming this access is correct as per runtime data

        // Log to verify the data being accessed
        logDebug('PavResults', 'subgradeResults bcrData:', bcrData, LogTag.CALCULATIONS);
        logDebug('PavResults', 'subgradeResults bcrSubgrade:', bcrSubgrade, LogTag.CALCULATIONS);
        logDebug('PavResults', 'subgradeResults bcrEmbarkment:', bcrEmbarkment, LogTag.CALCULATIONS);

        return [
            [t('unitWeightRes'), 'γ', bcrSubgrade?.unitWeight?.toString() || '', getMeasurementUnit(conversionLabel.kNm3_lbfft3)],
            [t('frictionAngleRes'), 'φ', bcrSubgrade?.frictionAngle?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
            [t('cohesionRes'), 'c', bcrSubgrade?.cohesion?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            // These lines use bcrEmbarkment, ensure it's correctly populated if these are also missing.
            // The primary issue reported is with subgrade properties like unitWeight, frictionAngle, cohesion.
        ];
    }, [projectInputs, currentProjectType, currentProjectMethod, t, getMeasurementUnit]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const embarkmentResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data - no embankment data for unpaved roads
        if (currentProjectType === EProjectType.unpaved) {
            return [];
        }

        const bcrData = (projectInputs as Record<string, any>).bcr;
        if (!bcrData) return [];

        const bcrEmbarkment = bcrData.embarkmentCharacteristics;
        return [
            [t('embarkment_weight', { unit_measure: '' }).trim(), 'γ', bcrEmbarkment?.unitWeight?.toString() || '', getMeasurementUnit(conversionLabel.kNm3_lbfft3)],
            [t('embarkment_friction_angle', { unit_measure: '' }).trim(), 'φ', bcrEmbarkment?.frictionAngle?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
            [t('embarkment_cohesion', { unit_measure: '' }).trim(), 'c', bcrEmbarkment?.cohesion?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            [t('loadSpreadingAngleRes'), 'αᵤ', bcrEmbarkment?.loadSpreadingAngleU?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
            [t('loadSpreadingAngleRes'), 'αᵣ', bcrEmbarkment?.loadSpreadingAngleR?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
        ];
    }, [projectInputs, currentProjectType, t, getMeasurementUnit]);

    // --- STATIC method input data (unified in one table, show only the selected subgrade input) ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const staticInputResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data - no static method for unpaved roads
        if (currentProjectType === EProjectType.unpaved) {
            return [];
        }

        const staticData = (projectInputs as Record<string, any>).static;
        if (!staticData) return [];

        // Determine subgrade input method based on which property is present
        const subgradeRows: string[][] = [];
        if (staticData.subgrade?.cbrSubgrade !== undefined) {
            subgradeRows.push([String(t('static_cbr', 'CBR')), 'CBR', staticData.subgrade.cbrSubgrade.toString(), '%']);
        } else if (staticData.subgrade?.deformationModulus !== undefined) {
            subgradeRows.push([String(t('static_deformation_modulus', 'Deformation Modulus')), 'E', staticData.subgrade.deformationModulus.toString(), 'MPa']);
        }

        let loadRows: string[][] = [];
        if (currentProjectType === EProjectType.container_yard) {
            const cyStaticLoad = staticData.load as IStaticLoadData; // Type assertion
            loadRows = [
                [String(t('static_deck_half_width', 'Deck Half Width')), 'B&rsquo;', cyStaticLoad?.deckHalfWidth?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [String(t('static_deck_half_length', 'Deck Half Length')), 'L&rsquo;', cyStaticLoad?.deckHalfLength?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [String(t('static_uniform_load', 'Uniform Load')), 'q', cyStaticLoad?.uniformLoad?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            ];
        } else if (currentProjectType === EProjectType.parking_deck) {
            const pdStaticLoad = staticData.load as ILoadDataWheel; // Type assertion
            loadRows = [
                [String(t('wheel_load', 'Wheel Load')), 'P', pdStaticLoad?.wheelLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [String(t('tire_inflation_pressure', 'Tire Inflation Pressure')), 'p', pdStaticLoad?.tireInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            ];
        }

        return [...loadRows, ...subgradeRows, [String(t('static_safety_factor', 'Safety Factor')), 'FS', staticData.fsDesign?.fsDesign?.toString() || '', '-']];
    }, [projectInputs, currentProjectType, t, getMeasurementUnit]);

    // The AASHTO design and, after the RUN, the stored inputs (InputForm onSubmit, state S) are SI: Imperial shows them
    // converted once, as Results_Paved. The Focus Design inputs stay in feet in state S (bead macread-fe-1vb).
    const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
    const toDisplayLength = useCallback((meters: number): number => (isImperial ? convert(meters).from('m').to('ft') : meters), [isImperial]);
    const metersPerDisplayUnit = isImperial ? convert(1).from('ft').to('m') : 1;

    // --- AIRPORT INPUTS TABLE ---
    const airportInputsTable = useMemo(() => {
        logDebug(
            'AirportResults',
            'Building airport inputs table',
            {
                currentProjectType,
                projectDataExists: !!projectData,
                projectData: projectData,
            },
            LogTag.FORM_SETUP
        );

        if (currentProjectType !== EProjectType.airport || !projectData) return [];

        // FLAT structure: data is directly on projectData, not nested under method
        const methodData = projectData as any;

        const rows: string[][] = [];
        const unitSystem = dynamicProject.data?.unit || 'Metric';

        // Formats a length field: m → ft with three decimals in Imperial, as Results_Paved
        const getConvertedValue = (value: unknown): string => {
            if (value === null || value === undefined) return '';
            const numValue = typeof value === 'number' ? value : parseFloat(value as string);
            if (isNaN(numValue)) return '';
            return isImperial ? toDisplayLength(numValue).toFixed(3) : numValue.toString();
        };

        const getUnit = (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        };

        // Road Info section
        if (methodData.roadInfo) {
            const roadInfo = methodData.roadInfo;
            if (roadInfo.carriagewayWidth !== undefined) {
                rows.push([t('width_of_carriageway_one_direction', 'Carriageway Width'), 'W', getConvertedValue(roadInfo.carriagewayWidth), getUnit('m', 'ft')]);
            }
            if (roadInfo.longitudinalSlope !== undefined) {
                rows.push([t('longitudinal_slope', 'Longitudinal Slope'), 'S', roadInfo.longitudinalSlope.toString(), '%']);
            }
            if (roadInfo.transversalSlope !== undefined) {
                rows.push([t('transversal_slope', 'Transversal Slope'), 'Sx', roadInfo.transversalSlope.toString(), '%']);
            }
        }

        // Traffic Load section
        if (methodData.trafficLoad) {
            const trafficLoad = methodData.trafficLoad;
            if (trafficLoad.functionalClassification) {
                rows.push([t('functional_classification', 'Functional Classification'), '', trafficLoad.functionalClassification, '']);
            }
            if (trafficLoad.roadType) {
                rows.push([t('road_type', 'Road Type'), '', trafficLoad.roadType, '']);
            }
            if (trafficLoad.reliabilityLevel !== undefined) {
                rows.push([t('reliability_level_r', 'Reliability Level'), 'R', trafficLoad.reliabilityLevel.toString(), '%']);
            }
            if (trafficLoad.combinedStandardError !== undefined) {
                rows.push([t('combined_standard_error_so', 'Combined Standard Error'), 'So', trafficLoad.combinedStandardError.toString(), '']);
            }
            if (trafficLoad.initialServiceIndex !== undefined) {
                rows.push([t('initial_service_index_po', 'Initial Service Index'), 'Po', trafficLoad.initialServiceIndex.toString(), '']);
            }
            if (trafficLoad.terminalSurfaceIndex !== undefined) {
                rows.push([t('terminal_surface_index_pt', 'Terminal Surface Index'), 'Pt', trafficLoad.terminalSurfaceIndex.toString(), '']);
            }
            if (trafficLoad.esalFinalValue !== undefined) {
                rows.push([t('esal_final_value', 'ESAL Final Value'), 'ESAL', trafficLoad.esalFinalValue.toString(), '']);
            }
        }

        // Surface section
        if (methodData.surface) {
            const surface = methodData.surface;
            if (surface.surfaceMaterial) {
                rows.push([t('select_surface_material', 'Surface Material'), '', t(surface.surfaceMaterial), '']);
            }
            if (surface.minThickness !== undefined) {
                rows.push([t('minimum_thickness_of_surface_layer', 'Min Surface Thickness'), 't', getConvertedValue(surface.minThickness), getUnit('m', 'ft')]);
            }
        }

        // Base Course section
        if (methodData.baseCourse) {
            const baseCourse = methodData.baseCourse;
            if (baseCourse.baseMaterial) {
                rows.push([t('select_base_material', 'Base Material'), '', t(baseCourse.baseMaterial), '']);
            }
            if (baseCourse.minThickness !== undefined) {
                rows.push([t('minimum_thickness_of_base_layer', 'Min Base Thickness'), 't', getConvertedValue(baseCourse.minThickness), getUnit('m', 'ft')]);
            }
            if (baseCourse.baseGeogrid && baseCourse.baseGeogrid.trim() !== '') {
                rows.push([t('select_geogrid_for_base_course', 'Base Geogrid'), '', baseCourse.baseGeogrid, '']);
            }
        }

        // Subbase Course section
        if (methodData.subbaseCourse) {
            const subbaseCourse = methodData.subbaseCourse;
            if (subbaseCourse.subbaseMaterial) {
                rows.push([t('select_subbase_material', 'Subbase Material'), '', t(subbaseCourse.subbaseMaterial), '']);
            }
            if (subbaseCourse.minThickness !== undefined) {
                rows.push([t('minimum_thickness_of_subbase_layer', 'Min Subbase Thickness'), 't', getConvertedValue(subbaseCourse.minThickness), getUnit('m', 'ft')]);
            }
            if (subbaseCourse.subbaseGeogrid && subbaseCourse.subbaseGeogrid.trim() !== '') {
                rows.push([t('select_geogrid_for_subbase_course', 'Subbase Geogrid'), '', subbaseCourse.subbaseGeogrid, '']);
            }
        }

        // Subgrade section - only show the selected method based on toggle
        const inputMethod = methodData.roadInfo?.subgradeInputMethod;
        if (methodData.subgrade && inputMethod) {
            const subgrade = methodData.subgrade;
            if (inputMethod === 'cbr') {
                if (subgrade.cbrSubgrade !== undefined) {
                    rows.push([t('cbr_subgrade_label', 'CBR Subgrade'), 'CBR', subgrade.cbrSubgrade.toString(), '%']);
                }
            } else if (inputMethod === 'ev2') {
                if (subgrade.ev2Subgrade !== undefined) {
                    rows.push([t('ev2_subgrade_label', 'Ev2 Subgrade'), 'Ev2', subgrade.ev2Subgrade.toString(), 'MPa']);
                }
            }
        }

        logDebug(
            'AirportResults',
            'Airport inputs table rows created',
            {
                rowsCount: rows.length,
                rows: rows.slice(0, 5), // Show first 5 rows
            },
            LogTag.FORM_SETUP
        );

        return rows;
    }, [currentProjectType, projectData, dynamicProject.data?.unit, dynamicProject.data?.method, t, isImperial, toDisplayLength]);

    // --- Prepara i dati per il diagramma NON RINFORZATO (Sinistra) ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const unreinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hu = processedOutput.deckDesign.unreinforcedHeight;
        if (hu === undefined) {
            logWarn('CYResults', 'Final unreinforcedHeight (hu) is undefined. Diagram will not render.', undefined, LogTag.VALIDATION);
            return null;
        }
        const baseThickUnreinforced = hu;
        const subgradeVisible = 0.5;
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickUnreinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        // Nessun geogrid per il non rinforzato
        return { baseCourse, subgrade, rightDimensionValue: hu, unitLabel, geogrid: undefined };
    }, [dynamicProject, processedOutput, t]);

    // --- Prepara i dati per il diagramma RINFORZATO (Destra) ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const reinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hr = processedOutput.deckDesign.reinforcedHeight;
        const currentSavingPercentage = processedOutput.deckDesign.thicknessSavingPercentage;
        if (hr === undefined) {
            logWarn('CYResults', 'Final reinforcedHeight (hr) is undefined. Diagram will not render.', undefined, LogTag.VALIDATION);
            return null;
        }
        const baseThickReinforced = hr;
        const subgradeVisible = 0.5;
        const firstBaseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.[0];
        const geogridInfo: IGeogridInfo | undefined = firstBaseGeogrid
            ? {
                  positionFromBottomOfBase: firstBaseGeogrid.position ?? 0,
                  label: firstBaseGeogrid.selectedGeogrid,
              }
            : undefined;
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickReinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: hr, savingPercentage: currentSavingPercentage, geogrid: geogridInfo, unitLabel };
    }, [dynamicProject, processedOutput, t]);

    // --- Prepara i dati per PAVED AASHTO - Diagramma UNSTABILIZED ---
    const pavedUnstabilizedGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.airport || !aashtoResults?.detailedCalculations?.softwareDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const { softwareDesign } = aashtoResults.detailedCalculations;

        const surfaceThickness = toDisplayLength(softwareDesign.surface.unstabilised);
        const baseThickness = toDisplayLength(softwareDesign.base.unstabilised);
        const subbaseThickness = toDisplayLength(softwareDesign.subbase.unstabilised);
        const subgradeVisible = toDisplayLength(0.5); // Visibile height for subgrade

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#D3D3D3' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            geogrid: undefined,
            subbaseGeogrid: undefined,
        };
    }, [dynamicProject, aashtoResults, currentProjectType, t, hasBaseGeogrid, hasSubbaseGeogrid, toDisplayLength]);

    // --- Prepara i dati per PAVED AASHTO - Diagramma STABILIZED ---
    const pavedStabilizedGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.airport || !aashtoResults?.detailedCalculations?.softwareDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const { softwareDesign } = aashtoResults.detailedCalculations;

        // The design table's stabilised column, also without geogrid: the drainage alone changes it (bead macread-fe-a0d)
        const surfaceThicknessRaw = softwareDesign.surface.stabilised;
        const baseThicknessRaw = softwareDesign.base.stabilised;
        const subbaseThicknessRaw = softwareDesign.subbase.stabilised;

        const surfaceThickness = toDisplayLength(surfaceThicknessRaw);
        const baseThickness = toDisplayLength(baseThicknessRaw);
        const subbaseThickness = toDisplayLength(subbaseThicknessRaw);
        const subgradeVisible = toDisplayLength(0.5); // Visibile height for subgrade

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#D3D3D3' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;
        const unstabilizedTotal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
        const stabilizedTotal = surfaceThicknessRaw + baseThicknessRaw + subbaseThicknessRaw;
        const savingPercentage = unstabilizedTotal > 0 ? ((unstabilizedTotal - stabilizedTotal) / unstabilizedTotal) * 100 : 0;

        // Get geogrid information from project data
        const firstBaseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.[0];
        const geogridInfo: IGeogridInfo | undefined = firstBaseGeogrid
            ? {
                  positionFromBottomOfBase: firstBaseGeogrid.position ?? 0,
                  label: firstBaseGeogrid.selectedGeogrid,
                  //   color: '#18c428',
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage,
            unitLabel,
            geogrid: geogridInfo,
        };
    }, [dynamicProject, aashtoResults, currentProjectType, t, toDisplayLength]);

    // --- Prepara i dati per PAVED AASHTO - Diagramma "INCREASED PASSAGES" con múltiple geogrids ---
    const pavedIncreasedPassagesGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.airport || !aashtoResults?.detailedCalculations?.softwareDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const { softwareDesign } = aashtoResults.detailedCalculations;

        // Use unstabilised values if no geogrid selected, otherwise use stabilised
        const surfaceThickness = softwareDesign.surface.stabilised;
        const baseThickness = hasBaseGeogrid ? softwareDesign.base.stabilised : softwareDesign.base.unstabilised;
        const subbaseThickness = hasSubbaseGeogrid ? softwareDesign.subbase.stabilised : softwareDesign.subbase.unstabilised;
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#D3D3D3' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // Get both base and subbase geogrid information
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const baseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.[0];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const subbaseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridSubBaseCourse?.geogrids?.[0];

        // Create geogrid info for base geogrid (positioned near base-subbase interface)
        // FLAT structure: data is directly on projectData
        const methodData = projectData as any;
        const baseGeogridSelection = methodData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = methodData?.subbaseCourse?.subbaseGeogrid;

        logDebug(
            'surfaceDesignIncreasedPassagesGraphicData',
            'Form geogrid selections:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                methodData: methodData?.baseCourse,
            },
            LogTag.AIRPORT
        );

        // Use dynamic label: prefer AASHTO result name, fallback to humanized form selection
        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        // Check if geogrid selections should be displayed
        const shouldShowBaseGeogrid = baseGeogridSelection && baseGeogridSelection !== 'none_selected' && !baseGeogridSelection.includes('none');

        const shouldShowSubbaseGeogrid = subbaseGeogridSelection && subbaseGeogridSelection !== 'none_selected' && !subbaseGeogridSelection.includes('none');

        logDebug(
            'surfaceDesignIncreasedPassagesGraphicData',
            'Geogrid display decisions:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                shouldShowBaseGeogrid,
                shouldShowSubbaseGeogrid,
            },
            LogTag.AIRPORT
        );

        // Conditionally create geogrid info for "Increased Passages" diagram using AASHTO results
        // Use the same label source as design sections for consistency
        const baseGeogridInfo: IGeogridInfo | undefined = shouldShowBaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness - 0.05, // Poco sopra la linea base-subbase
                  label: baseGeogridLabel, // Now uses AASHTO data directly like design sections
                  // color: '#18c428',
              }
            : undefined;

        // Create geogrid info for subbase geogrid (positioned near subbase-subgrade interface)
        const subbaseGeogridInfo: IGeogridInfo | undefined = shouldShowSubbaseGeogrid
            ? {
                  positionFromBottomOfBase: subbaseThickness - 0.05, // Poco sopra la linea subbase-subgrade (relativa al subbase)
                  label: subbaseGeogridLabel, // Now uses AASHTO data directly like design sections
                  // color: '#ff6b35', // Different color for subbase geogrid
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Conditionally include geogrid based on form selection
            geogrid: baseGeogridInfo,
            // Conditionally include subbase geogrid based on form selection
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [dynamicProject, aashtoResults, currentProjectType, t, hasBaseGeogrid, hasSubbaseGeogrid]);

    // --- Prepara i dati per UNPAVED GIROUD - Diagramma UNSTABILIZED ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const unpavedUnstabilizedGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved || !processedOutput || !dynamicProject.data) return null;
        const unpavedOutput = processedOutput as Record<string, any>;
        if (!unpavedOutput.unstabilisedRoadThickness) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const thickness = unpavedOutput.unstabilisedRoadThickness;
        const baseThick = thickness;
        const subgradeVisible = 0.5;
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThick, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: thickness, unitLabel, geogrid: undefined };
    }, [dynamicProject, processedOutput, currentProjectType, t]);

    // --- Prepara i dati per UNPAVED GIROUD - Diagramma GEOTEXTILE ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const unpavedGeotextileGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved || !hasGeotextileSelected || !processedOutput || !dynamicProject.data) return null;
        const unpavedOutput = processedOutput as Record<string, any>;
        if (!unpavedOutput.geotextileStabilisedThickness) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const thickness = unpavedOutput.geotextileStabilisedThickness;
        const savingPercentage = unpavedOutput.thicknessSavingGeotextile;
        const baseThick = thickness;
        const subgradeVisible = 0.5;
        const geogrid: IGeogridInfo = {
            positionFromBottomOfBase: baseThick,
            label: 'GTX',
            color: '#FF0000',
        };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThick, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: baseThick, savingPercentage, geogrid, unitLabel };
    }, [dynamicProject, processedOutput, currentProjectType, hasGeotextileSelected, t]);

    // --- Prepara i dati per UNPAVED GIROUD - Diagramma GEOGRID ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const unpavedGeogridGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved || !processedOutput || !dynamicProject.data) return null;
        const unpavedOutput = processedOutput as Record<string, any>;
        if (!unpavedOutput.geogridStabilisedThickness) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const thickness = unpavedOutput.geogridStabilisedThickness;
        const savingPercentage = unpavedOutput.thicknessSavingGeogrid;
        const baseThick = thickness;
        const subgradeVisible = 0.5;
        const geogrid: IGeogridInfo = {
            positionFromBottomOfBase: baseThick,
            label: 'GG',
            color: '#00FF00',
        };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThick, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: baseThick, savingPercentage, geogrid, unitLabel };
    }, [dynamicProject, processedOutput, currentProjectType, t]);

    // --- Prepara i dati per SURFACE DESIGN - Diagramma UNSTABILIZED ---
    const surfaceDesignUnstabilizedGraphicData = useMemo(() => {
        if (!isSurfaceDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Calcola lo spessore surface usando recalculateThickness
        const surfaceThickness = recalculateThickness('surface', surfaceDesignInputs, false);

        // Usa gli spessori fissi dal form per base e subbase
        const baseThickness = surfaceDesignInputs.baseThickness;
        const subbaseThickness = surfaceDesignInputs.subbaseThickness;
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            geogrid: undefined,
            subbaseGeogrid: undefined,
        };
    }, [isSurfaceDesignEnabled, aashtoResults, dynamicProject.data, surfaceDesignInputs, t, recalculateThickness]);

    // --- Prepara i dati per SURFACE DESIGN - Diagramma STABILIZED ---
    const surfaceDesignStabilizedGraphicData = useMemo(() => {
        if (!isSurfaceDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Calcola lo spessore surface usando recalculateThickness (no geogrid in surface layer)
        const surfaceThickness = recalculateThickness('surface', surfaceDesignInputs, false);

        // Usa gli spessori fissi dal form per base e subbase
        const baseThickness = surfaceDesignInputs.baseThickness;
        const subbaseThickness = surfaceDesignInputs.subbaseThickness;
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // Calcola risparmio percentuale
        const unstabilizedSurfaceThickness = recalculateThickness('surface', surfaceDesignInputs, false);
        const savingPercentage = unstabilizedSurfaceThickness > 0 ? ((unstabilizedSurfaceThickness - surfaceThickness) / unstabilizedSurfaceThickness) * 100 : 0;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage: Math.max(0, savingPercentage),
            unitLabel,
        };
    }, [isSurfaceDesignEnabled, aashtoResults, dynamicProject.data, surfaceDesignInputs, t, recalculateThickness]);

    // --- Prepara i dati per SURFACE DESIGN - Diagramma "INCREASED PASSAGES" con geogrids ---
    const surfaceDesignIncreasedPassagesGraphicData = useMemo(() => {
        if (!isSurfaceDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Calcola lo spessore surface usando recalculateThickness (no geogrid in surface layer)
        const surfaceThickness = recalculateThickness('surface', surfaceDesignInputs, false);

        // Usa gli spessori fissi dal form per base e subbase
        const baseThickness = surfaceDesignInputs.baseThickness;
        const subbaseThickness = surfaceDesignInputs.subbaseThickness;
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // FLAT structure: data is directly on projectData
        const methodData = projectData as any;
        const baseGeogridSelection = methodData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = methodData?.subbaseCourse?.subbaseGeogrid;

        // Use dynamic label: prefer AASHTO result name, fallback to humanized form selection
        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        logDebug(
            'surfaceDesignIncreasedPassagesGraphicData',
            'Labels:',
            {
                baseGeogridLabel,
                subbaseGeogridLabel,
            },
            LogTag.CHARTS
        );

        // Crea le informazioni per le geogrids (stessa logica dei diagrammi principali)
        const baseGeogridInfo: IGeogridInfo = {
            positionFromBottomOfBase: baseThickness * 0.65,
            label: baseGeogridLabel,
            // color: '#18c428',
        };

        const subbaseGeogridInfo: IGeogridInfo = {
            positionFromBottomOfBase: baseThickness + subbaseThickness * 0.65,
            label: subbaseGeogridLabel,
            // color: '#ff6b35',
        };

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Aggiungo informazioni per la base geogrid
            geogrid: baseGeogridInfo,
            // Aggiungo informazioni per la subbase geogrid
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [isSurfaceDesignEnabled, aashtoResults, dynamicProject.data, surfaceDesignInputs, t, recalculateThickness]);

    // --- Prepara i dati per BASE DESIGN - Diagramma UNSTABILIZED ---
    const baseDesignUnstabilizedGraphicData = useMemo(() => {
        if (!isBaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Usa gli spessori fissi dal form per surface e subbase
        const surfaceThickness = baseDesignInputs.surfaceThickness;
        const subbaseThickness = baseDesignInputs.subbaseThickness;
        // Calcola lo spessore base usando recalculateThickness
        const baseThickness = recalculateThickness('base', baseDesignInputs, false);
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            geogrid: undefined,
            subbaseGeogrid: undefined,
        };
    }, [isBaseDesignEnabled, aashtoResults, dynamicProject.data, baseDesignInputs, t, recalculateThickness, hasBaseGeogrid]);

    // --- Prepara i dati per BASE DESIGN - Diagramma STABILIZED ---
    const baseDesignStabilizedGraphicData = useMemo(() => {
        if (!isBaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Usa gli spessori fissi dal form per surface e subbase
        const surfaceThickness = baseDesignInputs.surfaceThickness;
        const subbaseThickness = baseDesignInputs.subbaseThickness;
        // Calcola lo spessore base usando recalculateThickness (stabilizzato solo se geogrid presente)
        const baseThickness = recalculateThickness('base', baseDesignInputs, hasBaseGeogrid);
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // Calcola risparmio percentuale
        const unstabilizedBaseThickness = recalculateThickness('base', baseDesignInputs, false);
        const savingPercentage = unstabilizedBaseThickness > 0 ? ((unstabilizedBaseThickness - baseThickness) / unstabilizedBaseThickness) * 100 : 0;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage: Math.max(0, savingPercentage),
            unitLabel,
        };
    }, [isBaseDesignEnabled, aashtoResults, dynamicProject.data, baseDesignInputs, t, recalculateThickness, hasBaseGeogrid]);

    // --- Prepara i dati per BASE DESIGN - Diagramma "INCREASED PASSAGES" con geogrids ---
    const baseDesignIncreasedPassagesGraphicData = useMemo(() => {
        if (!isBaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Usa gli spessori fissi dal form per surface e subbase
        const surfaceThickness = baseDesignInputs.surfaceThickness;
        const subbaseThickness = baseDesignInputs.subbaseThickness;
        // Calcola lo spessore base usando recalculateThickness (stabilizzato solo se geogrid presente)
        const baseThickness = recalculateThickness('base', baseDesignInputs, hasBaseGeogrid);
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // FLAT structure: data is directly on projectData
        const methodData = projectData as any;
        const baseGeogridSelection = methodData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = methodData?.subbaseCourse?.subbaseGeogrid;

        logDebug(
            'baseDesignIncreasedPassagesGraphicData',
            'Base design form geogrid selections:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                isEnabled: isBaseDesignEnabled,
            },
            LogTag.AIRPORT
        );

        // Use dynamic label: prefer AASHTO result name, fallback to humanized form selection
        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        logDebug(
            'baseDesignIncreasedPassagesGraphicData',
            'Labels:',
            {
                baseGeogridLabel,
                subbaseGeogridLabel,
            },
            LogTag.CHARTS
        );

        // Check if geogrid selections should be displayed for base design
        const shouldShowBaseGeogrid = baseGeogridSelection && baseGeogridSelection !== 'none_selected' && !baseGeogridSelection.includes('none');

        const shouldShowSubbaseGeogrid = subbaseGeogridSelection && subbaseGeogridSelection !== 'none_selected' && !subbaseGeogridSelection.includes('none');

        logDebug(
            'baseDesignIncreasedPassagesGraphicData',
            'Base design geogrid display decisions:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                shouldShowBaseGeogrid,
                shouldShowSubbaseGeogrid,
            },
            LogTag.AIRPORT
        );

        // Conditionally create le informazioni per le geogrids (stessa logica dei diagrammi principali)
        const baseGeogridInfo: IGeogridInfo | undefined = shouldShowBaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness * 0.65,
                  label: baseGeogridLabel,
                  // color: '#18c428',
              }
            : undefined;

        const subbaseGeogridInfo: IGeogridInfo | undefined = shouldShowSubbaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness + subbaseThickness * 0.65,
                  label: subbaseGeogridLabel,
                  // color: '#ff6b35',
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Aggiungo informazioni per la base geogrid
            geogrid: baseGeogridInfo,
            // Aggiungo informazioni per la subbase geogrid
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [isBaseDesignEnabled, aashtoResults, dynamicProject.data, baseDesignInputs, t, recalculateThickness, hasBaseGeogrid]);

    // --- Prepara i dati per SUBBASE DESIGN - Diagramma UNSTABILIZED ---
    const subbaseDesignUnstabilizedGraphicData = useMemo(() => {
        if (!isSubbaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Usa gli spessori fissi dal form per surface e base
        const surfaceThickness = subbaseDesignInputs.surfaceThickness;
        const baseThickness = subbaseDesignInputs.baseThickness;
        // Calcola lo spessore subbase usando recalculateThickness
        const subbaseThickness = recalculateThickness('subbase', subbaseDesignInputs, false);
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            geogrid: undefined,
            subbaseGeogrid: undefined,
        };
    }, [isSubbaseDesignEnabled, aashtoResults, dynamicProject.data, subbaseDesignInputs, t, recalculateThickness, hasSubbaseGeogrid]);

    // --- Prepara i dati per SUBBASE DESIGN - Diagramma STABILIZED ---
    const subbaseDesignStabilizedGraphicData = useMemo(() => {
        if (!isSubbaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Usa gli spessori fissi dal form per surface e base
        const surfaceThickness = subbaseDesignInputs.surfaceThickness;
        const baseThickness = subbaseDesignInputs.baseThickness;
        // Calcola lo spessore subbase usando recalculateThickness (stabilizzato solo se geogrid presente)
        const subbaseThickness = recalculateThickness('subbase', subbaseDesignInputs, hasSubbaseGeogrid);
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // Calcola risparmio percentuale
        const unstabilizedSubbaseThickness = recalculateThickness('subbase', subbaseDesignInputs, false);
        const savingPercentage = unstabilizedSubbaseThickness > 0 ? ((unstabilizedSubbaseThickness - subbaseThickness) / unstabilizedSubbaseThickness) * 100 : 0;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage: Math.max(0, savingPercentage),
            unitLabel,
        };
    }, [isSubbaseDesignEnabled, aashtoResults, dynamicProject.data, subbaseDesignInputs, t, recalculateThickness, hasSubbaseGeogrid]);

    // --- Prepara i dati per SUBBASE DESIGN - Diagramma "INCREASED PASSAGES" con geogrids ---
    const subbaseDesignIncreasedPassagesGraphicData = useMemo(() => {
        if (!isSubbaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // Usa gli spessori fissi dal form per surface e base
        const surfaceThickness = subbaseDesignInputs.surfaceThickness;
        const baseThickness = subbaseDesignInputs.baseThickness;
        // Calcola lo spessore subbase usando recalculateThickness (stabilizzato solo se geogrid presente)
        const subbaseThickness = recalculateThickness('subbase', subbaseDesignInputs, hasSubbaseGeogrid);
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // FLAT structure: data is directly on projectData
        const methodData = projectData as any;
        const baseGeogridSelection = methodData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = methodData?.subbaseCourse?.subbaseGeogrid;

        // Use dynamic label: prefer AASHTO result name, fallback to humanized form selection
        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        logDebug(
            'subbaseDesignIncreasedPassagesGraphicData',
            'Subbase design form geogrid selections:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                isEnabled: isSubbaseDesignEnabled,
            },
            LogTag.AIRPORT
        );

        logDebug(
            'subbaseDesignIncreasedPassagesGraphicData',
            'Labels:',
            {
                baseGeogridLabel,
                subbaseGeogridLabel,
            },
            LogTag.CHARTS
        );

        // Check if geogrid selections should be displayed for subbase design
        const shouldShowBaseGeogrid = baseGeogridSelection && baseGeogridSelection !== 'none_selected' && !baseGeogridSelection.includes('none');

        const shouldShowSubbaseGeogrid = subbaseGeogridSelection && subbaseGeogridSelection !== 'none_selected' && !subbaseGeogridSelection.includes('none');

        logDebug(
            'subbaseDesignIncreasedPassagesGraphicData',
            'Subbase design geogrid display decisions:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                shouldShowBaseGeogrid,
                shouldShowSubbaseGeogrid,
            },
            LogTag.AIRPORT
        );

        // Conditionally create le informazioni per le geogrids (stessa logica dei diagrammi principali)
        const baseGeogridInfo: IGeogridInfo | undefined = shouldShowBaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness * 0.65,
                  label: baseGeogridLabel,
                  // color: '#18c428',
              }
            : undefined;

        const subbaseGeogridInfo: IGeogridInfo | undefined = shouldShowSubbaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness + subbaseThickness * 0.65,
                  label: subbaseGeogridLabel,
                  // color: '#ff6b35',
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Aggiungo informazioni per la base geogrid
            geogrid: baseGeogridInfo,
            // Aggiungo informazioni per la subbase geogrid
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [isSubbaseDesignEnabled, aashtoResults, dynamicProject.data, subbaseDesignInputs, t, recalculateThickness, hasSubbaseGeogrid]);

    useEffect(() => {
        const mapDeckAnalysisData = (data: IDeckAnalysisDataItem[] | undefined): Point[] => {
            if (!data) return [];
            try {
                return data.map(item => ({
                    x: item.h,
                    y: item.fs,
                }));
            } catch (error) {
                logError('CYResults', 'Errore durante la mappatura dei dati di analisi del deck:', error, LogTag.ERROR_HANDLING);
                return [];
            }
        };

        if (processedOutput?.deckDesign?.unreinforcedDeckAnalysisData) {
            setUnreinforcedDeckTable(mapDeckAnalysisData(processedOutput.deckDesign.unreinforcedDeckAnalysisData));
        } else {
            setUnreinforcedDeckTable([]);
        }

        if (processedOutput?.deckDesign?.reinforcedDeckAnalysisData) {
            setReinforcedDeckTable(mapDeckAnalysisData(processedOutput.deckDesign.reinforcedDeckAnalysisData));
        } else {
            setReinforcedDeckTable([]);
        }

        // Use bcr field from reinforcedDeckAnalysisData for setBcrDeckTable (with type assertion)
        if (processedOutput?.deckDesign?.reinforcedDeckAnalysisData) {
            setBcrDeckTable(
                (processedOutput.deckDesign.reinforcedDeckAnalysisData as IDeckAnalysisDataItemWithBcr[])
                    .filter(item => typeof item.bcr === 'number')
                    .map(item => ({
                        x: item.h,
                        y: item.bcr as number,
                    }))
            );
        } else {
            setBcrDeckTable([]);
        }
    }, [processedOutput]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const deckResults = useMemo(() => {
        if (!processedOutput) return [];

        // Handle unpaved road results
        if (currentProjectType === EProjectType.unpaved) {
            const unpavedOutput = processedOutput as any; // Type assertion for unpaved structure
            return [
                [t('unstabilised_road_thickness', 'Unstabilised Road Thickness'), 'hu', unpavedOutput.unstabilisedRoadThickness?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [
                    t('geotextile_stabilised_thickness', 'Geotextile Stabilised Thickness'),
                    'hgtx',
                    unpavedOutput.geotextileStabilisedThickness?.toString() || '',
                    getMeasurementUnit(conversionLabel.m_ft),
                ],
                [t('geogrid_stabilised_thickness', 'Geogrid Stabilised Thickness'), 'hgg', unpavedOutput.geogridStabilisedThickness?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [t('thickness_saving_geotextile', 'Thickness Saving Geotextile'), 'Δh_gtx', unpavedOutput.thicknessSavingGeotextile?.toFixed(2) || '', '%'],
                [t('thickness_saving_geogrid', 'Thickness Saving Geogrid'), 'Δh_gg', unpavedOutput.thicknessSavingGeogrid?.toFixed(2) || '', '%'],
                [t('design_adequate', 'Design Adequate'), '', unpavedOutput.isDesignAdequate ? 'Yes' : 'No', ''],
            ];
        }

        // Handle container yard/parking deck results
        if (!processedOutput?.deckDesign) return [];
        // Round thicknessSavingPercentage to 3 decimals if present
        let roundedSavingPercentage = '';
        if (typeof processedOutput.deckDesign.thicknessSavingPercentage === 'number') {
            roundedSavingPercentage = processedOutput.deckDesign.thicknessSavingPercentage.toFixed(3);
        } else if (processedOutput.deckDesign.thicknessSavingPercentage != null) {
            roundedSavingPercentage = String(processedOutput.deckDesign.thicknessSavingPercentage);
        }
        return [
            [t('unstabilized_deck', { unit_measure: '' }), 'hᵤ', processedOutput.deckDesign.unreinforcedHeight?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
            [t('stabilized_deck', { unit_measure: '' }), 'hₛ', processedOutput.deckDesign.reinforcedHeight?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
            [t('thickness_saving', { unit_measure: 'm' }), 'Δh', processedOutput.deckDesign.thicknessSaving?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
            [t('thickness_saving_percentage', { unit_measure: 'm' }), 'Δh', roundedSavingPercentage, '%'],
        ];
    }, [processedOutput, currentProjectType, t, getMeasurementUnit]);

    // Compute the maximum h value from all chart data for x-axis scaling
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const computedMaxH = useMemo(() => {
        const allH = [...unreinforcedDeckTable.map(p => p.x), ...reinforcedDeckTable.map(p => p.x), ...bcrDeckTable.map(p => p.x)];
        return allH.length > 0 ? Math.max(...allH) : 0;
    }, [unreinforcedDeckTable, reinforcedDeckTable, bcrDeckTable]);

    // --- STATIC chart data mapping ---
    const staticChartData = useMemo(() => {
        if (currentProjectMethod === 'static' && processedOutput?.deckDesign && Array.isArray((processedOutput.deckDesign as any).iterationTable)) {
            // Estraggo i dati dalla tabella delle iterazioni
            const iterationTable = (processedOutput.deckDesign as any).iterationTable as {
                height: number;
                stabBearingCapacity: number;
                unstabBearingCapacity: number;
                verticalPressure: number;
            }[];

            if (!iterationTable || iterationTable.length === 0) return null;

            // Mappa i dati per ogni curva. Axes are swapped:
            // X-axis: q (kPa) values
            // Y-axis: H (m) values
            const vertPressure = iterationTable.map(item => ({
                x: item.verticalPressure, // X is now Pressure
                y: item.height, // Y is now Height
            }));
            const unstab = iterationTable.map(item => ({
                x: item.unstabBearingCapacity, // X is now Capacity
                y: item.height, // Y is now Height
            }));
            const stab = iterationTable.map(item => ({
                x: item.stabBearingCapacity, // X is now Capacity
                y: item.height, // Y is now Height
            }));

            return { vertPressure, unstab, stab };
        }
        return null;
    }, [currentProjectMethod, processedOutput]);

    // --- PARKING DECK STATIC chart data mapping (cuKPa vs. H) ---
    const parkingDeckStaticChartData = useMemo(() => {
        // Ensure EProjectType.parking_deck is correctly defined and imported
        if (currentProjectType === EProjectType.parking_deck && currentProjectMethod === 'static' && processedOutput?.deckDesign?.cbrLookupTable) {
            const cbrTable = processedOutput.deckDesign.cbrLookupTable;
            if (!Array.isArray(cbrTable) || cbrTable.length === 0) return null;

            const hUnstabilisedData = cbrTable.map(item => ({
                x: item.cuKPa, // X-axis: cuKPa
                y: item.hUnstabilisedM, // Y-axis: hUnstabilisedM
            }));

            const hStabilisedData = cbrTable.map(item => ({
                x: item.cuKPa, // X-axis: cuKPa
                y: item.hStabilisedM, // Y-axis: hStabilisedM
            }));
            logDebug('CYResults', 'parkingDeckStaticChartData prepared:', { hUnstabilisedData, hStabilisedData }, LogTag.CHARTS);
            return { hUnstabilisedData, hStabilisedData };
        }
        return null;
    }, [currentProjectType, currentProjectMethod, processedOutput]);

    // This function renders the new AASHTO results from the Redux store.
    const renderPavedResults = () => {
        if (currentProjectType !== EProjectType.airport || !aashtoResults) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { totalSN, requiredThicknesses, finalSN, verification } = aashtoResults;

        return (
            <Paper sx={{ p: 3, mt: 2, mb: 3, backgroundColor: '#fafafa' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    AASHTO &apos;93 Pavement Design Results
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Overall Structural Number (SN)
                            </Typography>
                            <Typography variant="body1">
                                <strong>Total Required SN:</strong> {totalSN.toFixed(3)} inches
                            </Typography>
                            <Typography variant="body1">
                                <strong>Final Provided SN:</strong> {finalSN.toFixed(3)} inches
                            </Typography>
                            <Typography variant="body2" color={verification.isAdequate ? 'success.main' : 'error.main'} sx={{ mt: 1 }}>
                                {verification.message}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Detailed Excel Calculations Table */}
                {aashtoResults.detailedCalculations && (
                    <Paper sx={{ p: 3, mt: 2, backgroundColor: '#f5f5f5' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Detailed AASHTO Calculations (Excel Format)
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Basic Parameters */}
                            <Grid item xs={12} md={4}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Basic AASHTO Parameters
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>ΔPSI = po - pt</strong>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {aashtoResults?.detailedCalculations?.basicParameters?.dpsi !== undefined
                                                        ? aashtoResults.detailedCalculations.basicParameters.dpsi.toFixed(1)
                                                        : 'N/A (dpsi undefined)'}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Standard normal deviate ZR</strong>
                                                </TableCell>
                                                <TableCell align="right">{aashtoResults.detailedCalculations.basicParameters.zr.toFixed(3)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Structural Number SN</strong>
                                                </TableCell>
                                                <TableCell align="right">{aashtoResults.detailedCalculations.basicParameters.structuralNumberSN.toFixed(3)}</TableCell>
                                                <TableCell>inches</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell align="right">{(aashtoResults.detailedCalculations.basicParameters.structuralNumberSN * 0.0254).toFixed(3)}</TableCell>
                                                <TableCell>m</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Mr - kPa</strong>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <strong>Mr - psi</strong>
                                                </TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Subgrade</TableCell>
                                                <TableCell align="right">{aashtoResults.detailedCalculations.basicParameters.mrSubgradeKpa.toFixed(0)}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.basicParameters.mrSubgradePsi.toFixed(0)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Layer Coefficients */}
                            <Grid item xs={12} md={8}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Layer Parameters
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Layer</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <strong>a</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <strong>Unstabilised m</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <strong>Stabilised m</strong>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Surface layer 1</strong>
                                                </TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.surface.a1.toFixed(2)}</TableCell>
                                                <TableCell align="center">1.00</TableCell>
                                                <TableCell align="center">1.00</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Base course 2</strong>
                                                </TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.base.a2.toFixed(2)}</TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.base.m2.toFixed(2)}</TableCell>
                                                <TableCell align="center">
                                                    {(aashtoResults.detailedCalculations.layerCoefficients.base.m2_stabilised || aashtoResults.detailedCalculations.layerCoefficients.base.m2).toFixed(
                                                        2
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Subbase 3</strong>
                                                </TableCell>
                                                <TableCell align="center">{(Math.ceil(aashtoResults.detailedCalculations.layerCoefficients.subbase.a3 * 100) / 100).toFixed(2)}</TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.subbase.m3.toFixed(2)}</TableCell>
                                                <TableCell align="center">
                                                    {(
                                                        aashtoResults.detailedCalculations.layerCoefficients.subbase.m3_stabilised || aashtoResults.detailedCalculations.layerCoefficients.subbase.m3
                                                    ).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>

                                    {/* LCR Values */}
                                    {(aashtoResults.detailedCalculations.layerCoefficients.base.lcr || aashtoResults.detailedCalculations.layerCoefficients.subbase.lcr) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                <strong>LCR Values</strong>
                                            </Typography>
                                            <Table size="small">
                                                <TableBody>
                                                    {aashtoResults.detailedCalculations.layerCoefficients.base.lcr && (
                                                        <TableRow>
                                                            <TableCell>
                                                                <strong>Base Stabilised LCR</strong>
                                                            </TableCell>
                                                            <TableCell>{aashtoResults.detailedCalculations.layerCoefficients.base.lcr.toFixed(6)}</TableCell>
                                                        </TableRow>
                                                    )}
                                                    {aashtoResults.detailedCalculations.layerCoefficients.subbase.lcr && (
                                                        <TableRow>
                                                            <TableCell>
                                                                <strong>SubBase Stabilised LCR</strong>
                                                            </TableCell>
                                                            <TableCell>{aashtoResults.detailedCalculations.layerCoefficients.subbase.lcr.toFixed(6)}</TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    )}
                                </Card>
                            </Grid>

                            {/* ESAL Factors */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        ESAL & Calculation Factors
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>f1 (|ZR| × So)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.f1.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>f2 (log term)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.f2.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>f3 (Mr term)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.f3.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>LogW18 (Unstabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.logW18Unstabilised.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>LogW18 (Stabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.logW18Stabilised.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>ESAL (Unstabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.esalUnstabilised.toExponential(3)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>ESAL (Stabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.esalStabilised.toExponential(3)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>TBR (Traffic Benefit Ratio)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(2)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Structural Numbers (SN) */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Structural Numbers (SN)
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN Unstabilised</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.snUnstabilised.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN Stabilised</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.snStabilised.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN Requested</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.totalSN.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN1&apos; (Surface)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.sn1Prime.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN2&apos; (Base)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.sn2Prime.toFixed(2)}</TableCell>
                                            </TableRow>
                                            {aashtoResults.detailedCalculations.snCalculations.sn3Prime && (
                                                <TableRow>
                                                    <TableCell>
                                                        <strong>SN3&apos; (Subbase)</strong>
                                                    </TableCell>
                                                    <TableCell>{aashtoResults.detailedCalculations.snCalculations.sn3Prime.toFixed(2)}</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Design Thickness Breakdown */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Design Layer Thickness (meters)
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Layer</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <strong>Unstabilised</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <strong>Stabilised</strong>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Surface</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.surface.unstabilised.toFixed(3) || 'N/A'}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.surface.stabilised.toFixed(3) || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Base</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.base.unstabilised.toFixed(3) || 'N/A'}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.base.stabilised.toFixed(3) || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Subbase</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.subbase.unstabilised.toFixed(3) || 'N/A'}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.subbase.stabilised.toFixed(3) || 'N/A'}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {/* Design_LCR Final Thickness Comparison */}
                {aashtoResults.detailedCalculations?.designLCRThickness && (
                    <Paper sx={{ p: 3, mt: 2, backgroundColor: '#e8f5e8' }}>
                        <Grid container spacing={3}>
                            {/* Show message if no design analysis is enabled */}
                            {!isSurfaceDesignEnabled && !isBaseDesignEnabled && !isSubbaseDesignEnabled && (
                                <Grid item xs={12}>
                                    <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                                            📋 No Design Analysis Enabled
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            Enable one or more design analysis options in the form to see thickness comparison tables:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            • Surface Design Analysis
                                            <br />
                                            • Base Design Analysis
                                            <br />• Subbase Design Analysis
                                        </Typography>
                                    </Card>
                                </Grid>
                            )}

                            {/* Surface Design Focus */}
                            {isSurfaceDesignEnabled && (
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ p: 2 }}>
                                        {/* Excel-style Design SURFACE layer thickness table */}
                                        <Table size="small" sx={{ border: '2px solid #333', '& .MuiTableCell-root': { border: '1px solid #666' } }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e0e0e0' }} colSpan={5}>
                                                        Design SURFACE layer thickness
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Unstabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Stabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Surface</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {recalculateThickness('surface', surfaceDesignInputs, false).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {recalculateThickness('surface', surfaceDesignInputs, false).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Base</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subbase</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Card>
                                </Grid>
                            )}

                            {/* Base Design Focus */}
                            {isBaseDesignEnabled && (
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ p: 2 }}>
                                        {/* Excel-style Design BASE layer thickness table */}
                                        <Table size="small" sx={{ border: '2px solid #333', '& .MuiTableCell-root': { border: '1px solid #666' } }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e0e0e0' }} colSpan={5}>
                                                        Design BASE layer thickness
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Unstabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Stabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Surface</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Base</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>{recalculateThickness('base', baseDesignInputs, false).toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>{recalculateThickness('base', baseDesignInputs, true).toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subbase</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Card>
                                </Grid>
                            )}

                            {/* Subbase Design Focus */}
                            {isSubbaseDesignEnabled && (
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ p: 2 }}>
                                        {/* Excel-style Design SUBBASE layer thickness table */}
                                        <Table size="small" sx={{ border: '2px solid #333', '& .MuiTableCell-root': { border: '1px solid #666' } }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e0e0e0' }} colSpan={5}>
                                                        Design SUBBASE layer thickness
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Unstabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Stabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Surface</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Base</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subbase</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {recalculateThickness('subbase', subbaseDesignInputs, false).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {recalculateThickness('subbase', subbaseDesignInputs, hasSubbaseGeogrid).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                )}
        </Paper>
        );
    };

    // --- Chart rendering logic ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const renderChartSection = () => {
        // For unpaved projects, check if chart data is available
        if (currentProjectType === EProjectType.unpaved) {
            const unpavedOutput = processedOutput as any;
            if (unpavedOutput?.chartData && Array.isArray(unpavedOutput.chartData) && unpavedOutput.chartData.length > 0) {
                logDebug('PavResults', 'Rendering Unpaved (Giroud) Chart with chartData', undefined, LogTag.CHARTS);

                const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
                const hLabel = isImperial ? t('h_ft_axis_label', 'H (ft)') : t('h_m_axis_label', 'H (m)');
                const pLabel = isImperial ? t('p_psi_axis_label', 'P (psi)') : t('p_kpa_axis_label', 'P (kPa)');

                // Map chart data to the expected format
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const unreinforcedData = unpavedOutput.chartData.map((item: any) => ({
                    x: item.h,
                    y: item.p_UNR,
                }));

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const geotextileData = unpavedOutput.chartData.map((item: any) => ({
                    x: item.h,
                    y: item.p_GTX,
                }));

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const geogridData = unpavedOutput.chartData.map((item: any) => ({
                    x: item.h,
                    y: item.p_GG,
                }));

                return (
                    <PavementLineChartComponent
                        data={unpavedOutput.chartData} // Use the chartData directly from API
                        xAxisLabel={hLabel}
                        yAxisLabel={pLabel}
                        method={'unpaved'} // This will use "UNR", "GTX", "GG" labels
                    />
                );
            } else {
                logDebug('PavResults', 'Unpaved project - no chart data available', undefined, LogTag.CHARTS);
                return null;
            }
        }

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const hLabel = isImperial ? t('h_ft_axis_label', 'H (ft)') : t('h_m_axis_label', 'H (m)');
        const qLabel = isImperial ? t('q_psi_axis_label', 'q (psi)') : t('q_kpa_axis_label', 'q (kPa)');
        const cuLabel = isImperial ? t('cu_psi_axis_label', 'Cu (psi)') : t('cukpa_axis_label', 'Cu (kPa)');

        if (currentProjectType === EProjectType.parking_deck && currentProjectMethod === 'static' && parkingDeckStaticChartData) {
            logDebug('CYResults', 'Rendering Parking Deck Static Chart', undefined, LogTag.CHARTS);
            return (
                <PavementLineChartComponent
                    data={parkingDeckStaticChartData.hStabilisedData} // Maps to legend dataKey 'reinforced'
                    reinforcedData={true}
                    xAxisLabel={cuLabel as string}
                    yAxisLabel={hLabel as string}
                    method={'paved'} // Standard paved method for regular legend labels
                />
            );
        } else if (currentProjectMethod === 'static' && staticChartData && staticChartData.vertPressure.length > 0) {
            logDebug('CYResults', 'Rendering Standard Static Chart for Container Yard', undefined, LogTag.CHARTS);
            return (
                <PavementLineChartComponent
                    data={staticChartData.stab} // stab data: { x: stabCapacity, y: height }
                    reinforcedData={true}
                    bcrData={true}
                    xAxisLabel={qLabel} // X-axis is now Pressure/Capacity
                    yAxisLabel={hLabel} // Y-axis is now Height
                    method={'paved'} // Standard paved method for regular legend labels
                />
            );
        }
        // Default: grafico classico
        return (
            <PavementLineChartComponent
                data={unreinforcedDeckTable}
                reinforcedData={true}
                bcrData={true}
                xAxisLabel={hLabel}
                yAxisLabel={t('fs_axis_label', 'FS') as string}
                method={'paved'} // Standard paved method for regular legend labels
            />
        );
    };

    // Unused render functions removed - dead code

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [aashtoAccordionExpanded, setAashtoAccordionExpanded] = useState<boolean>(false);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // qui setto la dimensione forzata del tire layers standard degli strati
    // Larghezze coerenti con Results.tsx (unpaved)
    // ...dichiarazioni duplicate rimosse...
    // NB: se serve la logica textile, va gestita separatamente
    // TODO: devo passare anche la X
    const [unstabilizedStageHeight, setUnstabilizedStageHeight] = useState<number | undefined>(undefined);

    // Larghezze coerenti con Results.tsx (unpaved)
    // NB: rimuovere eventuali dichiarazioni precedenti di queste variabili!
    const rectWidth = 360;
    const rectWidthGrid = 200;
    // 210 è il label + a occhio quello che occupa la quota e il numero
    const forcedStageWidth = containerWidth > 0 ? rectWidth + 210 : undefined;
    const forcedStageWidthGrid = 290; // ex: 320

    // ...existing code...
    // Rimuovo eventuali altre dichiarazioni di rectWidth, rectWidthGrid, forcedStageWidth più in basso nel file
    return (
        <Grid container id="results-component-container" ref={containerRef}>
            {currentProjectType === EProjectType.airport && (
                <Accordion expanded={aashtoAccordionExpanded} onChange={() => setAashtoAccordionExpanded(!aashtoAccordionExpanded)} sx={{ width: '100%', display: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">AASHTO &apos;93 Pavement Design Results</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{renderPavedResults()}</AccordionDetails>
                </Accordion>
            )}

            {/* For airport projects: show unstabilized and stabilized diagrams */}
{currentProjectType === EProjectType.airport && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center">
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('unstabilised_title', 'UNSTABILISED')}
                        </Typography>
                        <div id="tire-layer-unstabilized" className="cont-grafico" style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                            {pavedUnstabilizedGraphicData ? (
                                <TireAndLayersAirportPaved
                                    stageType="unstabilized"
                                    surfaceLayer={pavedUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={pavedUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={pavedUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={pavedUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={pavedUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    geogrid={pavedUnstabilizedGraphicData?.geogrid}
                                    subbaseGeogrid={pavedUnstabilizedGraphicData?.subbaseGeogrid}
                                    showGeogrid={!!pavedUnstabilizedGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!pavedUnstabilizedGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={pavedUnstabilizedGraphicData.unitLabel}
                                    metersPerUnit={metersPerDisplayUnit}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                    onStageHeightCalculated={h => setUnstabilizedStageHeight(h)}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Information Display under first diagram */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, mx: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Traffic Analysis
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: ESAL (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">ESAL:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                    {/* Placeholder for second diagram (if needed) */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                        </Typography>
                        <div
                            id="tire-layer-increased-passages"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                            {pavedUnstabilizedGraphicData ? (
                                <TireAndLayersAirportPaved
                                    stageType="increased-passages"
                                    surfaceLayer={pavedUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={pavedUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={pavedUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={pavedUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={pavedUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    geogrid={pavedIncreasedPassagesGraphicData?.geogrid}
                                    subbaseGeogrid={pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeogrid={!!pavedIncreasedPassagesGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={pavedUnstabilizedGraphicData.unitLabel}
                                    metersPerUnit={metersPerDisplayUnit}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    onStageHeightCalculated={h => setUnstabilizedStageHeight(h)}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Benefit Information Display under second diagram */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, mx: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Traffic Benefit Analysis
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(
                                                    (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                    aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: TBR (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TBR:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                            {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                    {/* Stabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('stabilised_title', 'STABILISED')}
                        </Typography>
                        <div
                            id="tire-layer-geogrid"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', minHeight: '300px' }}>
                            {pavedStabilizedGraphicData ? (
                                <TireAndLayersAirportPaved
                                    stageType="geogrid"
                                    unstabilizedStageHeight={unstabilizedStageHeight}
                                    surfaceLayer={pavedStabilizedGraphicData.surfaceLayer}
                                    baseLayer={pavedStabilizedGraphicData.baseLayer}
                                    subbaseLayer={pavedStabilizedGraphicData.subbaseLayer}
                                    subgrade={pavedStabilizedGraphicData.subgrade}
                                    rightDimensionValue={pavedStabilizedGraphicData.rightDimensionValue}
                                    savingPercentage={pavedStabilizedGraphicData.savingPercentage}
                                    geogrid={pavedIncreasedPassagesGraphicData?.geogrid}
                                    subbaseGeogrid={pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={true}
                                    showGeogrid={!!pavedIncreasedPassagesGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={pavedStabilizedGraphicData.unitLabel}
                                    metersPerUnit={metersPerDisplayUnit}
                                    projectType={currentProjectType}
                                    forcedStageWidth={320}
                                    rectWidth={rectWidthGrid}
                                    alignFromBottom={true}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Information Display under third diagram - Same traffic as first but with reduced thickness */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, mx: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Optimized Design
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: Thickness Saving (always shown) */}
                                    {pavedStabilizedGraphicData?.savingPercentage && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Thickness Saving:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                {pavedStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* Surface Design Diagrams - Solo se Surface Design è abilitato */}
            {currentProjectType === EProjectType.airport && currentProjectMethod === 'asphalt' && isSurfaceDesignEnabled && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center" sx={{ mt: 3 }}>
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('unstabilised_title', 'UNSTABILISED')}
                            </Typography>
                            <div
                                id="surface-design-unstabilized"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {surfaceDesignUnstabilizedGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="surface-design-unstabilized"
                                        surfaceLayer={surfaceDesignUnstabilizedGraphicData.surfaceLayer}
                                        baseLayer={surfaceDesignUnstabilizedGraphicData.baseLayer}
                                        subbaseLayer={surfaceDesignUnstabilizedGraphicData.subbaseLayer}
                                        subgrade={surfaceDesignUnstabilizedGraphicData.subgrade}
                                        rightDimensionValue={surfaceDesignUnstabilizedGraphicData.rightDimensionValue}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={false}
                                        geogrid={surfaceDesignUnstabilizedGraphicData?.geogrid}
                                        subbaseGeogrid={surfaceDesignUnstabilizedGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!surfaceDesignUnstabilizedGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!surfaceDesignUnstabilizedGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={surfaceDesignUnstabilizedGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Traffic Information Display under Surface Design Unstabilized */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Surface Design - Original
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                        maximumFractionDigits: 0,
                                                    }) ?? 'N/A'}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: ESAL (always shown) */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">ESAL:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Grid>

                        {/* Stabilized with Savings */}
                        <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                            </Typography>
                            <div
                                id="surface-design-increased-passages"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {surfaceDesignStabilizedGraphicData && surfaceDesignUnstabilizedGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="surface-design-increased-passages"
                                        surfaceLayer={surfaceDesignUnstabilizedGraphicData.surfaceLayer}
                                        baseLayer={surfaceDesignUnstabilizedGraphicData.baseLayer}
                                        subbaseLayer={surfaceDesignUnstabilizedGraphicData.subbaseLayer}
                                        subgrade={surfaceDesignUnstabilizedGraphicData.subgrade}
                                        rightDimensionValue={surfaceDesignUnstabilizedGraphicData.rightDimensionValue}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={false}
                                        geogrid={surfaceDesignIncreasedPassagesGraphicData?.geogrid}
                                        subbaseGeogrid={surfaceDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!surfaceDesignIncreasedPassagesGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!surfaceDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={surfaceDesignUnstabilizedGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Traffic Benefit Information Display under Surface Design Increased Passages */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Surface Design - Traffic Increase
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(
                                                        (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                        aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                    ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: TBR (always shown) */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">TBR:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                                {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Grid>

                        {/* Increased Passages with Geogrids */}
                        <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('stabilised_title', 'STABILISED')}
                            </Typography>
                            <div
                                id="surface-design-stabilized"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {surfaceDesignIncreasedPassagesGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="surface-design-stabilized"
                                        surfaceLayer={surfaceDesignIncreasedPassagesGraphicData.surfaceLayer}
                                        baseLayer={surfaceDesignIncreasedPassagesGraphicData.baseLayer}
                                        subbaseLayer={surfaceDesignIncreasedPassagesGraphicData.subbaseLayer}
                                        subgrade={surfaceDesignIncreasedPassagesGraphicData.subgrade}
                                        rightDimensionValue={surfaceDesignIncreasedPassagesGraphicData.rightDimensionValue}
                                        savingPercentage={surfaceDesignStabilizedGraphicData?.savingPercentage}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={true}
                                        geogrid={surfaceDesignIncreasedPassagesGraphicData?.geogrid}
                                        subbaseGeogrid={surfaceDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!surfaceDesignIncreasedPassagesGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!surfaceDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={surfaceDesignIncreasedPassagesGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Optimized Design Information Display under Surface Design Stabilized */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Surface Design - Optimized
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                        maximumFractionDigits: 0,
                                                    }) ?? 'N/A'}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: Thickness Saving (always shown) */}
                                        {surfaceDesignStabilizedGraphicData?.savingPercentage && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Thickness Saving:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                    {surfaceDesignStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
            )}

            {/* Base Design Diagrams - Solo se Base Design è abilitato */}
            {currentProjectType === EProjectType.airport && currentProjectMethod === 'asphalt' && isBaseDesignEnabled && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center" sx={{ mt: 3 }}>
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('unstabilised_title', 'UNSTABILISED')}
                            </Typography>
                            <div
                                id="base-design-unstabilized"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {baseDesignUnstabilizedGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="base-design-unstabilized"
                                        surfaceLayer={baseDesignUnstabilizedGraphicData.surfaceLayer}
                                        baseLayer={baseDesignUnstabilizedGraphicData.baseLayer}
                                        subbaseLayer={baseDesignUnstabilizedGraphicData.subbaseLayer}
                                        subgrade={baseDesignUnstabilizedGraphicData.subgrade}
                                        rightDimensionValue={baseDesignUnstabilizedGraphicData.rightDimensionValue}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={false}
                                        geogrid={baseDesignUnstabilizedGraphicData?.geogrid}
                                        subbaseGeogrid={baseDesignUnstabilizedGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!baseDesignUnstabilizedGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!baseDesignUnstabilizedGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={baseDesignUnstabilizedGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Traffic Information Display under Base Design Unstabilized */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Base Design - Original
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                        maximumFractionDigits: 0,
                                                    }) ?? 'N/A'}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: ESAL (always shown) */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">ESAL:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Grid>

                        {/* Increased Passages */}
                        <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                            </Typography>
                            <div
                                id="base-design-increased-passages"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {baseDesignStabilizedGraphicData && baseDesignUnstabilizedGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="base-design-increased-passages"
                                        surfaceLayer={baseDesignUnstabilizedGraphicData.surfaceLayer}
                                        baseLayer={baseDesignUnstabilizedGraphicData.baseLayer}
                                        subbaseLayer={baseDesignUnstabilizedGraphicData.subbaseLayer}
                                        subgrade={baseDesignUnstabilizedGraphicData.subgrade}
                                        rightDimensionValue={baseDesignUnstabilizedGraphicData.rightDimensionValue}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={false}
                                        geogrid={baseDesignIncreasedPassagesGraphicData?.geogrid}
                                        subbaseGeogrid={baseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!baseDesignIncreasedPassagesGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!baseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={baseDesignUnstabilizedGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Traffic Benefit Information Display under Base Design Increased Passages */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Base Design - Traffic Increase
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(
                                                        (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                        aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                    ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: TBR (always shown) */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">TBR:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                                {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Grid>

                        {/* Stabilized */}
                        <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('stabilised_title', 'STABILISED')}
                            </Typography>
                            <div
                                id="base-design-stabilized"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {baseDesignIncreasedPassagesGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="base-design-stabilized"
                                        surfaceLayer={baseDesignIncreasedPassagesGraphicData.surfaceLayer}
                                        baseLayer={baseDesignIncreasedPassagesGraphicData.baseLayer}
                                        subbaseLayer={baseDesignIncreasedPassagesGraphicData.subbaseLayer}
                                        subgrade={baseDesignIncreasedPassagesGraphicData.subgrade}
                                        rightDimensionValue={baseDesignIncreasedPassagesGraphicData.rightDimensionValue}
                                        savingPercentage={baseDesignStabilizedGraphicData?.savingPercentage}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={true}
                                        geogrid={baseDesignIncreasedPassagesGraphicData?.geogrid}
                                        subbaseGeogrid={baseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!baseDesignIncreasedPassagesGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!baseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={baseDesignIncreasedPassagesGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Optimized Design Information Display under Base Design Stabilized */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Base Design - Optimized
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                        maximumFractionDigits: 0,
                                                    }) ?? 'N/A'}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: Thickness Saving (always shown) */}
                                        {baseDesignStabilizedGraphicData?.savingPercentage && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Thickness Saving:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                    {baseDesignStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
            )}

            {/* Subbase Design Diagrams - Solo se Subbase Design è abilitato */}
            {currentProjectType === EProjectType.airport && currentProjectMethod === 'asphalt' && isSubbaseDesignEnabled && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center" sx={{ mt: 3 }}>
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('unstabilised_title', 'UNSTABILISED')}
                            </Typography>
                            <div
                                id="subbase-design-unstabilized"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {subbaseDesignUnstabilizedGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="subbase-design-unstabilized"
                                        surfaceLayer={subbaseDesignUnstabilizedGraphicData.surfaceLayer}
                                        baseLayer={subbaseDesignUnstabilizedGraphicData.baseLayer}
                                        subbaseLayer={subbaseDesignUnstabilizedGraphicData.subbaseLayer}
                                        subgrade={subbaseDesignUnstabilizedGraphicData.subgrade}
                                        rightDimensionValue={subbaseDesignUnstabilizedGraphicData.rightDimensionValue}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={false}
                                        geogrid={subbaseDesignUnstabilizedGraphicData?.geogrid}
                                        subbaseGeogrid={subbaseDesignUnstabilizedGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!subbaseDesignUnstabilizedGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!subbaseDesignUnstabilizedGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={subbaseDesignUnstabilizedGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Traffic Information Display under Subbase Design Unstabilized */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Subbase Design - Original
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                        maximumFractionDigits: 0,
                                                    }) ?? 'N/A'}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: ESAL (always shown) */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">ESAL:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Grid>

                        {/* Increased Passages */}
                        <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                            </Typography>
                            <div
                                id="subbase-design-increased-passages"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {subbaseDesignStabilizedGraphicData && subbaseDesignUnstabilizedGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="subbase-design-increased-passages"
                                        surfaceLayer={subbaseDesignUnstabilizedGraphicData.surfaceLayer}
                                        baseLayer={subbaseDesignUnstabilizedGraphicData.baseLayer}
                                        subbaseLayer={subbaseDesignUnstabilizedGraphicData.subbaseLayer}
                                        subgrade={subbaseDesignUnstabilizedGraphicData.subgrade}
                                        rightDimensionValue={subbaseDesignUnstabilizedGraphicData.rightDimensionValue}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={false}
                                        geogrid={subbaseDesignIncreasedPassagesGraphicData?.geogrid}
                                        subbaseGeogrid={subbaseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!subbaseDesignIncreasedPassagesGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!subbaseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={subbaseDesignUnstabilizedGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Traffic Benefit Information Display under Subbase Design Increased Passages */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Subbase Design - Traffic Increase
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(
                                                        (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                        aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                    ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: TBR (always shown) */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">TBR:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                                {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Grid>

                        {/* Stabilized */}
                        <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t('stabilised_title', 'STABILISED')}
                            </Typography>
                            <div
                                id="subbase-design-stabilized"
                                className="cont-grafico"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }}>
                                {subbaseDesignIncreasedPassagesGraphicData ? (
                                    <TireAndLayersAirportPaved
                                        stageType="subbase-design-stabilized"
                                        surfaceLayer={subbaseDesignIncreasedPassagesGraphicData.surfaceLayer}
                                        baseLayer={subbaseDesignIncreasedPassagesGraphicData.baseLayer}
                                        subbaseLayer={subbaseDesignIncreasedPassagesGraphicData.subbaseLayer}
                                        subgrade={subbaseDesignIncreasedPassagesGraphicData.subgrade}
                                        rightDimensionValue={subbaseDesignIncreasedPassagesGraphicData.rightDimensionValue}
                                        savingPercentage={subbaseDesignStabilizedGraphicData?.savingPercentage}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={true}
                                        geogrid={subbaseDesignIncreasedPassagesGraphicData?.geogrid}
                                        subbaseGeogrid={subbaseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeogrid={!!subbaseDesignIncreasedPassagesGraphicData?.geogrid}
                                        showSubbaseGeogrid={!!subbaseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                        showGeotextile={false}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={subbaseDesignIncreasedPassagesGraphicData.unitLabel}
                                        projectType={currentProjectType}
                                        forcedStageWidth={forcedStageWidthGrid}
                                        rectWidth={rectWidthGrid}
                                        hideLayerTitles={true}
                                        rectStartXOffset={-90}
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>

                            {/* Optimized Design Information Display under Subbase Design Stabilized */}
                            {aashtoResults?.detailedCalculations && (
                                <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                        Subbase Design - Optimized
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                        {esalMode === 'automatic' && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                        maximumFractionDigits: 0,
                                                    }) ?? 'N/A'}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Row 2: Thickness Saving (always shown) */}
                                        {subbaseDesignStabilizedGraphicData?.savingPercentage && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Thickness Saving:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                    {subbaseDesignStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
            )}

            {/* AIRPORT INPUTS TABLE - Moved to bottom */}
            {currentProjectType === EProjectType.airport && airportInputsTable.length > 0 && (
                <Grid item xs={12} container spacing={0} justifyContent="center" alignItems="center" className="ntk-tabella" style={{ marginBottom: '20px', marginTop: '40px' }}>
                    <Grid item xs={10}>
                        <Typography className="table-title" style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold' }}>
                            {t('inputs_table', 'Inputs')}
                        </Typography>
                        <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={airportInputsTable} />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default Results_Airport;
